import { i18n } from "@lingui/core"

import {
	AssetMatchBrand,
	ContractType,
	Floor,
	Landlord,
	Link,
	OrderType,
	SearchFilter,
	Zone,
} from "models"

export enum AssetType {
	WAREHOUSE_OR_STORE = "WAREHOUSE_OR_STORE", //
	PLOT_OF_LAND = "PLOT_OF_LAND", //
}

class AssetTypeTranslations {
	WAREHOUSE_OR_STORE = /*i18n*/i18n._({ id: "enums.asset-types.WAREHOUSE_OR_STORE", message: "Immobile" })
	PLOT_OF_LAND = /*i18n*/i18n._({ id: "enums.asset-types.PLOT_OF_LAND", message: "Terreno" })
}

export enum PositionType {
	URBAN = "URBAN",
	SUBURBAN = "SUBURBAN", //
	HIGH_STREET = "HIGH_STREET", //
	MALL = "MALL", //
	RETAIL_PARK = "RETAIL_PARK", //
	STANDALONE = "STANDALONE", //
}
class PositionTypeTranslations {
	URBAN = /*i18n*/i18n._({ id: "enums.asset-position.URBAN", message: "Urbano" })
	SUBURBAN = /*i18n*/i18n._({ id: "enums.asset-position.SUBURBAN", message: "Periferia" })
	HIGH_STREET = /*i18n*/i18n._({ id: "enums.asset-position.HIGH_STREET", message: "Centro / Strada principale" })
	MALL = /*i18n*/i18n._({ id: "enums.asset-position.MALL", message: "Centro commerciale" })
	RETAIL_PARK = /*i18n*/i18n._({ id: "enums.asset-position.RETAIL_PARK", message: "Centro commerciale all'aperto" })
	STANDALONE = /*i18n*/i18n._({ id: "enums.asset-position.STANDALONE", message: "Standalone" })
}

export enum AssetStatus {
	AVAILABLE = "AVAILABLE",
	NOT_AVAILABLE = "NOT_AVAILABLE",
	NEGOTIATING = "NEGOTIATING",
}

class AssetStatusTranslations {
	AVAILABLE = /*i18n*/i18n._({ id: "enums.asset-status.AVAILABLE", message: "Disponibile" })
	NOT_AVAILABLE = /*i18n*/i18n._({ id: "enums.asset-status.NOT_AVAILABLE", message: "Non disponibile" })
	NEGOTIATING = /*i18n*/i18n._({ id: "enums.asset-status.NEGOTIATING", message: "In contrattazione" })
}

export enum AssetProvenience {
	CATASTO = "CATASTO",
	IMMOBILIARE = "IMMOBILIARE",
	SELECTED_BY_STOREBOOK = "SELECTED_BY_STOREBOOK",
	FRONTEND = "FRONTEND",
	SUBITO = "SUBITO",
	IDEALISTA = "IDEALISTA",
	URBISTAT = "URBISTAT"
}

class AssetProcenienceTranslations {
	CATASTO = /*i18n*/i18n._({ id: "enums.asset-provenience.CATASTO", message: "Catasto" })
	IMMOBILIARE = /*i18n*/i18n._({ id: "enums.asset-provenience.IMMOBILIARE", message: "Immobiliare.it" })
	SELECTED_BY_STOREBOOK = /*i18n*/i18n._({ id: "enums.asset-provenience.SELECTED_BY_STOREBOOK", message: "Unito" })
	FRONTEND = /*i18n*/i18n._({ id: "enums.asset-provenience.FRONTEND", message: "Frontend" })
	SUBITO = /*i18n*/i18n._({id: "enums.asset-provenience.SUBITO", message: "Subito" })
	IDEALISTA = /*i18n*/i18n._({id: "enums.asset-provenience.IDEALISTA", message: "Idealista" })
	URBISTAT = /*i18n*/i18n._({id: "enums.asset-provenience.URBISTAT", message: "Urbistat" })
	null = /*i18n*/i18n._({ id: "enums.asset-provenience.null", message: "-" })
}

export interface GMapsAnalysisEntry {
	count: number
	nearest_distance_meters: number
	nearest_coordinates: number[]
}

export interface BrandPosition {
	brand: string
	lat: number
	long: number
}

export interface AssetDetails {
	aiMarkdownResume: string | null
	aiAnalysis?: unknown | null
	weekendTrafficIncrease: number | null
	pedestrianPassHours: string | null
	totalFootCountDaily: number | null
	brandPositions: BrandPosition[] | null
	averageIncome: number | null
	averageAge: number | null
	highRankingEducationPercentage: number | null
	aiPriceMin: number | null
	aiPriceMax: number | null
	averageMqPriceInArea: number | null
}

export interface AssetCharacteristics {
	id: string
	lastUpdate: Date
	type: AssetType
	status: AssetStatus
	gla: number
	position: PositionType
	divisible: boolean
	shopWindows: number
	contractType: ContractType
	height: number
	airConditioning: boolean
	fireProtection: boolean
	toilet: boolean
	price: number
	fees: number
	entryFee: number
	parkingSlots: number
	floors: Floor[]
	energyClass: string
	gmapsAnalysis: { [id: string]: GMapsAnalysisEntry }
}

export interface Point {
	type: string
	coordinates: number[]
}

export interface Asset {
	id: string
	address: string
	location: Point
	zone: Zone
	region: string
	province: string
	city: string
	description: string
	creationDate: Date
	lastUpdate: Date
	catastoFoglio: string
	catastoMappale: string
	catastoParticella: string
	catastoSubalterno: string
	catastoCategoria: string
	links: Link[]
	landlord: Landlord
	matches: AssetMatchBrand[]
	characteristics: AssetCharacteristics
	aiAnalysis?: unknown | null
	assetDetails?: AssetDetails | null
	planimetry: string | null
	provenience: AssetProvenience | null
}

export interface AssetWithImage {
	asset: Asset,
	image: string
}

export interface AssetWithCheck extends Asset {
	checked: boolean
}

export interface CreateAssetRequest {
	address: string
	landlordId: string
}

export interface CreateAssetResponse {
	id: string
}

export class AssetFilter extends SearchFilter {
	address?: string | null
	city?: string | null
	province?: string | null
	region?: string | null
	type?: AssetType | null
	positionType?: PositionType | null
	divisible?: boolean | null
	shopWindows?: number | null
	contractType?: ContractType | null
	airConditioning?: boolean | null
	parkingSlots?: boolean | null
	provenience?: AssetProvenience | null

	glaMin?: number | null
	glaMax?: number | null
	priceMin?: number | null
	priceMax?: number | null

	pageSize: number = 10
	pageId: number = 0
	orderType: OrderType | null = null
	orderBy: string | null = null
}

export interface GeoAssetFilter extends SearchFilter {
	southWestLatitude: number
	southWestLongitude: number
	northEastLatitude: number
	northEastLongitude: number
	priorityId: string | null
}

export interface MergeAssetRequest {
	ids: string[]
}
